<!--
  DecorationArtThumb -- decoration artwork shown OFF a garment, on a
  backdrop it is visible on.

  The ONE mechanism for every off-product decoration image (library tiles,
  the store editor's Decorations rows, the product editor's artwork picker
  and variant cards, the batch order editor's logo picker, approval
  proofs). Composited-on-garment renders do not use it -- there the
  garment is the backdrop.

  The root IS the <img>, so a parent's class / data-testid / alt / loading
  land on it unchanged and existing sizing classes keep working. The
  backdrop is painted as the image's own inline background (it shows
  through transparent pixels and the object-fit letterbox), which beats
  any class-level background a parent sets on the same element.

  Decision (utils/artBackdrop.js): server-sampled ink -> a sample of THIS
  loaded <img>, only when its pixels are readable without CORS (data:,
  blob:, same-origin; never a CDN image -- see artBackdrop.js) -> the
  caller's ``unknownBackdrop`` (neutral by default). Two-tone ink
  (``inkMixed`` from the server, or a mixed client sample; #4079) goes on
  the neutral backdrop whatever its average. ``data-backdrop`` (light|dark|neutral|checker) and
  ``data-backdrop-source`` (server|sampled|pending|unknown|override) are rendered
  for tests and ui-runner scenarios.
-->
<template>
    <img
        data-testid="decoration-art-thumb"
        :src="src"
        :style="{ background: backdropCss }"
        :data-backdrop="backdropKey"
        :data-backdrop-source="backdropSource"
        @load="onLoad"
        @error="onError"
    />
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import {
    ART_BACKDROP_CSS,
    canReadPixels,
    pickArtBackdrop,
    resolveServerArtBackdrop,
    sampleImageInk,
} from '@/utils/artBackdrop';

const props = defineProps({
    /** Image URL. */
    src: { type: String, default: null },
    /**
     * Server-sampled ink color (``Artwork.dominant_ink_hex``). Pass the
     * raw value -- null/undefined means "not known". Never pass a display
     * placeholder.
     */
    inkHex: { type: String, default: null },
    /**
     * Server flag (``Artwork.dominant_ink_mixed``, #4079): the art is
     * strongly two-tone, so its average ink suits neither backdrop. Mixed
     * art goes on the neutral backdrop.
     */
    inkMixed: { type: Boolean, default: false },
    /**
     * 'auto' (default) decides from the ink. 'light' | 'dark' | 'checker'
     * is a manual override (the detail page's backdrop switcher).
     */
    backdrop: {
        type: String,
        default: 'auto',
        validator: (v) => ['auto', 'light', 'dark', 'checker'].includes(v),
    },
    /**
     * Backdrop when the ink cannot be known. Neutral keeps both white and
     * black art visible; a surface whose previous look must not regress
     * (the client portal proof) passes 'light'.
     */
    unknownBackdrop: {
        type: String,
        default: 'neutral',
        validator: (v) => ['neutral', 'light'].includes(v),
    },
});

const emit = defineEmits(['resolved']);

// Sample for the CURRENT src: undefined = none yet, null = unreadable,
// {hex, mixed} = sampled ink. `awaitingSample` is true from mount/src change until
// the visible image loads (or errors) and the sample settles.
const sampledHex = ref(undefined);
const awaitingSample = ref(false);

const serverPick = computed(() => resolveServerArtBackdrop(props.inkHex, props.inkMixed));

const wantsSample = computed(
    () => props.backdrop === 'auto' && !serverPick.value && canReadPixels(props.src)
);

const resolved = computed(() => {
    if (props.backdrop !== 'auto') {
        return { key: props.backdrop, source: 'override' };
    }
    if (serverPick.value) {
        return { key: serverPick.value, source: 'server' };
    }
    const sample = sampledHex.value;
    const sampledPick = pickArtBackdrop(sample && sample.hex);
    if (sampledPick) {
        return { key: sample.mixed ? 'neutral' : sampledPick, source: 'sampled' };
    }
    if (awaitingSample.value) return { key: props.unknownBackdrop, source: 'pending' };
    return { key: props.unknownBackdrop, source: 'unknown' };
});

const backdropKey = computed(() => resolved.value.key);
const backdropSource = computed(() => resolved.value.source);
const backdropCss = computed(() => ART_BACKDROP_CSS[backdropKey.value]);

watch(
    () => [props.src, wantsSample.value],
    ([, wants]) => {
        sampledHex.value = undefined;
        awaitingSample.value = wants;
    },
    { immediate: true }
);

// Sample the VISIBLE image once it has loaded: no second request, and a
// loading="lazy" image is never fetched just to be sampled.
function onLoad(event) {
    if (!wantsSample.value) return;
    const img = event.target;
    const src = props.src;
    sampleImageInk(img).then((hex) => {
        // A newer src may have replaced this one while it was in flight.
        if (props.src !== src) return;
        sampledHex.value = hex;
        awaitingSample.value = false;
    });
}

function onError() {
    awaitingSample.value = false;
}

watch(backdropKey, (key) => emit('resolved', key), { immediate: true });
</script>
